/* @jsx h */
import { defineComponent, h, computed } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { CatalogEntry } from "../state";

/** Display order and icon per catalog group; unknown groups sort last. */
const GROUP_META: Record<string, { order: number; icon: string }> = {
  sales: { order: 0, icon: "point_of_sale" },
  reductions: { order: 1, icon: "sell" },
  operations: { order: 2, icon: "tune" },
};

export const ReportCatalog = defineComponent({
  name: "DataRoomReportCatalog",
  props: {
    entries: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    onSelect: { type: Function, required: true },
  },
  setup(props: any) {
    const { t } = useLocalization();

    const grouped = computed(() => {
      const groups: Record<string, CatalogEntry[]> = {};
      for (const e of props.entries as CatalogEntry[]) {
        (groups[e.group] ??= []).push(e);
      }
      return Object.entries(groups).sort(
        ([a], [b]) =>
          (GROUP_META[a]?.order ?? 99) - (GROUP_META[b]?.order ?? 99),
      );
    });

    return () => {
      if (props.loading) {
        return (
          <div class="flex justify-center py-20">
            <span class="loading loading-spinner loading-lg text-primary opacity-60" />
          </div>
        );
      }
      return (
        <div class="w-full max-w-6xl mx-auto py-6 sm:py-8">
          {/*
            Rows rather than the old padded card grid: a two-column grid of
            small tiles still leaves an awkward gap whenever a group's count
            is odd, but compact rows read fine alone, and a wider max-width
            lets two columns of them fit comfortably as more reports arrive.
          */}
          <div class="flex flex-col gap-5">
            {grouped.value.map(([group, entries]) => (
              <section
                key={group}
                class="rounded-2xl border bg-base-200 p-3 shadow-sm sm:p-4"
              >
                <header class="mb-3 flex items-center gap-3 px-1">
                  <span class="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-base-100 text-primary">
                    <span class="material-symbols-outlined text-xl">
                      {GROUP_META[group]?.icon ?? "folder"}
                    </span>
                  </span>
                  <div class="min-w-0 flex-1">
                    <h3 class="flex items-center gap-2 text-sm font-semibold text-accent">
                      {t(`reports.dataroom.group.${group}`)}
                      <span class="badge badge-primary badge-outline badge-sm">
                        {entries.length}
                      </span>
                    </h3>
                    <p class="text-xs text-base-content/65">
                      {t(`reports.dataroom.group.${group}_description`)}
                    </p>
                  </div>
                </header>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {entries.map((e) => (
                    <button
                      key={e.definitionId}
                      class="group text-left rounded-xl bg-base-100 px-4 py-3.5 hover:bg-base-200/60 transition-colors flex gap-3.5 items-center"
                      onClick={() => props.onSelect(e.definitionId)}
                    >
                      <span class="grid place-items-center h-10 w-10 shrink-0 rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-content transition-colors">
                        <span class="material-symbols-outlined text-lg">
                          {e.icon}
                        </span>
                      </span>
                      <div class="min-w-0 flex-1">
                        <div class="font-semibold text-accent text-sm">
                          {t(e.nameKey)}
                        </div>
                        <div class="text-xs text-base-content/70 mt-0.5 truncate">
                          {t(e.descriptionKey)}
                        </div>
                      </div>
                      <span class="material-symbols-outlined text-base text-base-content/40 shrink-0 -translate-x-1 group-hover:translate-x-0 group-hover:text-primary transition-all">
                        arrow_forward
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      );
    };
  },
});
